import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  AlertCircle, 
  Search, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Globe,
  Smartphone,
  Monitor,
  TrendingUp,
  Shield,
  Zap,
  Target
} from 'lucide-react';
import SEOLoadingState from "@/components/seo/SEOLoadingState";
import SEOErrorState from "@/components/seo/SEOErrorState";
import { useSEOAudit } from '@/hooks/useSEOAudit';
import { FreeAuditData, FreeAuditCheck } from "@/types/seo";

function safeStringifyDetails(details: unknown): string {
  if (details == null) return '';
  if (typeof details === 'string' || typeof details === 'number' || typeof details === 'boolean') return String(details);
  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return '';
  }
}

export default function Overview() {
  const { audit, loading, error } = useSEOAudit();

  const freeAuditData = audit as FreeAuditData | undefined;

  const categorizedResults = useMemo(() => {
    if (!freeAuditData?.results) return {};

    const categories: Record<string, Array<{ key: string; checkName: string; check: FreeAuditCheck }>> = {
      'Basic SEO': [],
      'Advanced SEO': [],
      'Performance': [],
      'Security': []
    };

    Object.entries(freeAuditData.results).forEach(([key, check]) => {
      const parts = key.split(': ');
      if (parts.length < 2) return;

      const apiCategory = parts[0];
      const checkName = parts.slice(1).join(': ');
      const item = { key, checkName, check };

      if (categories[apiCategory]) {
        categories[apiCategory].push(item);
      }
    });

    return categories;
  }, [freeAuditData]);

  const getStatusIcon = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case 'pass':
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case 'fail':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-slate-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case 'pass':
        return <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100">Pass</Badge>;
      case 'fail':
        return <Badge variant="destructive" className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100">Fail</Badge>;
      case 'warning':
        return <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100">Warning</Badge>;
      case 'na':
        return <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">N/A</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Basic SEO':
        return <Target className="h-5 w-5 text-blue-500" />;
      case 'Advanced SEO':
        return <TrendingUp className="h-5 w-5 text-purple-500" />;
      case 'Performance':
        return <Zap className="h-5 w-5 text-orange-500" />;
      case 'Security':
        return <Shield className="h-5 w-5 text-green-500" />;
      default:
        return <Search className="h-5 w-5 text-slate-500" />;
    }
  };

  if (error) {
    return <SEOErrorState title="Unable to Load SEO Analysis" message={error} />;
  }

  if (loading) {
    return <SEOLoadingState message="Loading SEO audit data..." />;
  }

  if (!audit) {
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <Alert className="border-blue-200 bg-blue-50/50">
          <Globe className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-900">No Website Selected</AlertTitle>
          <AlertDescription className="text-blue-700">
            Please select a brand to view the SEO analysis and recommendations.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!freeAuditData) {
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <Alert className="border-amber-200 bg-amber-50/50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900">No Audit Data Available</AlertTitle>
          <AlertDescription className="text-amber-700">
            We couldn't retrieve SEO audit data for this website. Please try refreshing or contact support if the issue persists.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { stats, search_preview } = freeAuditData;
  const score = stats?.score ?? 0;
  const passedCount = stats?.passed ?? 0;
  const failedCount = stats?.failed ?? 0;
  const warningsCount = stats?.warnings ?? 0;
  const totalCount = stats?.total ?? 1;

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-emerald-600';
    if (score >= 75) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 75) return 'Good';
    if (score >= 60) return 'Fair';
    if (score >= 40) return 'Poor';
    return 'Critical';
  };

  const getProgressColor = (score: number) => {
    if (score >= 90) return '[&>*]:bg-emerald-500';
    if (score >= 75) return '[&>*]:bg-green-500';
    if (score >= 60) return '[&>*]:bg-amber-500';
    if (score >= 40) return '[&>*]:bg-orange-500';
    return '[&>*]:bg-red-500';
  };

  return (
    <div className="container max-w-7xl mx-auto space-y-6 py-6">
      {/* Header */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">SEO Overview</h1>
          <p className="text-muted-foreground mt-2">
            Comprehensive analysis of your website's search engine optimization performance and recommendations for improvement.
          </p>
        </div>
      </div>

      {/* SEO Score Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="relative overflow-hidden border-2">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20" />
          <CardHeader className="relative pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <Search className="h-4 w-4" />
              Overall SEO Score
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-4xl font-bold ${getScoreColor(score)}`}>
                  {score}
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  {getScoreLabel(score)}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-semibold text-slate-400 dark:text-slate-500">
                  / 100
                </div>
              </div>
            </div>
            <Progress 
              value={score} 
              className={`mt-3 h-2 ${getProgressColor(score)}`} 
            />
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20" />
          <CardHeader className="relative pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Passed Checks
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-emerald-600">{passedCount}</div>
              <div className="text-right">
                <div className="text-sm text-slate-500">of {totalCount}</div>
                <div className="text-xs text-slate-400">checks</div>
              </div>
            </div>
            <Progress 
              value={(passedCount / totalCount) * 100} 
              className="mt-3 h-2 [&>*]:bg-emerald-500" 
            />
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/20 dark:to-rose-950/20" />
          <CardHeader className="relative pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              Failed Checks
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-red-600">{failedCount}</div>
              <div className="text-right">
                <div className="text-sm text-slate-500">critical</div>
                <div className="text-xs text-slate-400">issues</div>
              </div>
            </div>
            <Progress 
              value={(failedCount / totalCount) * 100} 
              className="mt-3 h-2 [&>*]:bg-red-500" 
            />
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20" />
          <CardHeader className="relative pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Warnings
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-amber-600">{warningsCount}</div>
              <div className="text-right">
                <div className="text-sm text-slate-500">need</div>
                <div className="text-xs text-slate-400">attention</div>
              </div>
            </div>
            <Progress 
              value={(warningsCount / totalCount) * 100} 
              className="mt-3 h-2 [&>*]:bg-amber-500" 
            />
          </CardContent>
        </Card>
      </div>

      {/* Search Preview */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-600" />
            Search Engine Preview
          </CardTitle>
          <CardDescription>
            How your website appears in Google search results across different devices
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Desktop Preview */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                <Monitor className="h-4 w-4" />
                Desktop Preview
              </div>
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800/50 space-y-2">
                <div className="text-blue-600 dark:text-blue-400 text-lg font-medium line-clamp-1 hover:underline cursor-pointer">
                  {search_preview?.desktop?.title || 'Page title not available'}
                </div>
                <div className="text-green-600 dark:text-green-400 text-sm line-clamp-1">
                  {search_preview?.desktop?.url || 'URL not available'}
                </div>
                <div className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2 leading-relaxed">
                  {search_preview?.desktop?.description || 'Meta description not available'}
                </div>
              </div>
            </div>

            {/* Mobile Preview */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                <Smartphone className="h-4 w-4" />
                Mobile Preview
              </div>
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800/50 space-y-2 max-w-sm">
                <div className="text-blue-600 dark:text-blue-400 text-base font-medium line-clamp-2 hover:underline cursor-pointer">
                  {search_preview?.mobile?.title || 'Page title not available'}
                </div>
                <div className="text-green-600 dark:text-green-400 text-xs line-clamp-1">
                  {search_preview?.mobile?.url || 'URL not available'}
                </div>
                <div className="text-slate-600 dark:text-slate-400 text-xs line-clamp-3 leading-relaxed">
                  {search_preview?.mobile?.description || 'Meta description not available'}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SEO Audit Results by Category */}
      {Object.keys(freeAuditData.results).length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(categorizedResults).map(([category, checks]) => {
            if (!checks || checks.length === 0) return null;

            const categoryStats = {
              passed: checks.filter(c => c.check.status.toLowerCase() === 'pass').length,
              failed: checks.filter(c => c.check.status.toLowerCase() === 'fail').length,
              warnings: checks.filter(c => c.check.status.toLowerCase() === 'warning').length
            };

            return (
              <Card key={category} className="shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(category)}
                      {category}
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-xs">
                        {checks.length} checks
                      </Badge>
                    </div>
                  </CardTitle>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-emerald-600">
                      <CheckCircle className="h-3 w-3" />
                      {categoryStats.passed}
                    </div>
                    <div className="flex items-center gap-1 text-amber-600">
                      <AlertTriangle className="h-3 w-3" />
                      {categoryStats.warnings}
                    </div>
                    <div className="flex items-center gap-1 text-red-600">
                      <XCircle className="h-3 w-3" />
                      {categoryStats.failed}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {checks.map(({ key, checkName, check }) => (
                      <div key={key} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
                        <div className="mt-0.5 flex-shrink-0">
                          {getStatusIcon(check.status)}
                        </div>
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-medium text-sm text-slate-900 dark:text-slate-100">
                              {checkName}
                            </h4>
                            {getStatusBadge(check.status)}
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-400 break-words leading-relaxed">
                            {check.message}
                          </p>
                          {check.details ? (
                            <details className="group">
                              <summary className="text-xs text-blue-600 dark:text-blue-400 cursor-pointer hover:underline">
                                View technical details
                              </summary>
                              <pre className="mt-2 p-2 bg-slate-100 dark:bg-slate-900 rounded text-xs overflow-auto max-h-32 whitespace-pre-wrap break-all text-slate-700 dark:text-slate-300">
                                {safeStringifyDetails(check.details)}
                              </pre>
                            </details>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              SEO Audit Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400">
                No detailed SEO audit results were found for this analysis.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}